import { SELLING_UOM_DECIMAL_SHIFT } from '@snap-types/models/enums';
import { Decimal } from '@snap-types/types/decimal';
import { z } from 'zod/v4';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';
import { parseNumberInput, validateDecimalPrecision } from './number-input';

type ValidateQuantityParams = {
  quantity: string | Decimal;
  quoteLineUuid: string;
  quotePriceUuid: string;

  /**
   * Decides whether the quantity is a piece count or a count of services, which
   * is the only thing this validator needs the line for. Each caller already
   * knows — the entry handlers by which of the two they are, the reconciler
   * because it has looked the line up for its own conversions.
   */
  isServiceCharge?: boolean;
};

export function validateQuantity(
  state: WritableQuoteBuilderStateType,
  {
    quantity,
    quoteLineUuid,
    quotePriceUuid,
    isServiceCharge = false,
  }: ValidateQuantityParams,
) {
  const quantityString = quantity.toString();

  const invalidIssueId = buildIssueId({
    area: 'quotePrice',
    quoteLineUuid,
    quotePriceUuid,
    type: 'invalid',
    field: 'quantity',
  });

  const parsed = parseNumberInput({
    value: quantityString,
    requiredMessage: 'Quantity is required',
    invalidMessage: 'Quantity must be a number',
  });

  if (parsed.error) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid quantity',
      description: parsed.error,
      severity: 'error',
      type: 'invalid',
      field: 'quantity',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'quantity',
        }),
      },
    });
  }

  /**
   * A quantity is typed in the site's selling unit but stored in eaches, so
   * what the field has to accept is any value landing on a whole piece. That is
   * exactly the unit's decimal shift — a site selling in eaches admits no
   * fraction, one selling in thousands admits three places, and a fourth would
   * be half a piece.
   *
   * Service charges go the other way. Their quantity counts services rather
   * than pieces and is never converted, so it is a whole number at every site.
   */
  const maxDecimalPlaces = isServiceCharge
    ? 0
    : SELLING_UOM_DECIMAL_SHIFT[state.sellingUnitOfMeasure];

  const precisionError = validateDecimalPrecision({
    value: quantityString,
    maxDecimalPlaces,
    maxIntegerDigits: 15,
    decimalPlacesMessage:
      maxDecimalPlaces === 0
        ? 'Quantity must be a whole number'
        : `Quantity cannot have more than ${maxDecimalPlaces} decimal places`,
    integerDigitsMessage:
      'Quantity cannot have more than 15 digits before the decimal point',
  });

  if (precisionError) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid quantity',
      description: precisionError,
      severity: 'error',
      type: 'invalid',
      field: 'quantity',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'quantity',
        }),
      },
    });
  }

  const validator = z
    .number({
      error: 'Quantity must be a number',
    })
    .positive({ message: 'Quantity must be greater than 0' });

  const result = validator.safeParse(parsed.parsed);

  if (!result.success) {
    const error = z.flattenError(result.error).formErrors[0];

    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid quantity',
      description: error,
      severity: 'error',
      type: 'invalid',
      field: 'quantity',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'quantity',
        }),
      },
    });
  }

  deleteIssue(state, invalidIssueId);

  return new Decimal(result.data);
}
